import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Mic, Trash2, ChevronRight, Search, Volume2, X } from "lucide-react";
import { getAllSessions, deleteSession, clearAllSessions, getSessionStats } from "../lib/sessionStore";
import Card from "../components/atoms/Card";
import Chip from "../components/atoms/Chip";
import Button from "../components/atoms/Button";

function formatTime(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  const days = Math.floor((now - d) / (1000 * 60 * 60 * 24));
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function durationOf(s) {
  if (!s.endedAt) return "ongoing";
  const ms = s.endedAt - s.startedAt;
  const min = Math.round(ms / 60000);
  if (min < 1) return "<1m";
  return `${min}m`;
}

const MODE_TONE = {
  scenario: "ink",
  topic:    "gold",
  free:     "rust",
};

export default function History() {
  const [sessions, setSessions] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [openSession, setOpenSession] = useState(null);

  useEffect(() => { setSessions(getAllSessions()); }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stats = useMemo(() => getSessionStats(), [sessions]);

  const filtered = useMemo(() => {
    return sessions.filter((s) => {
      if (filter !== "all" && s.mode !== filter) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (s.title?.toLowerCase().includes(q) ||
              s.messages?.some((m) => m.text?.toLowerCase().includes(q)));
    });
  }, [sessions, search, filter]);

  function handleDelete(id, e) {
    e.preventDefault(); e.stopPropagation();
    if (window.confirm("Delete this session permanently?")) {
      deleteSession(id);
      setSessions(getAllSessions());
    }
  }

  function handleClearAll() {
    if (window.confirm(`Delete ALL ${sessions.length} sessions? This cannot be undone.`)) {
      clearAllSessions();
      setSessions([]);
    }
  }

  function speak(text) {
    if (!("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "en-US"; u.rate = 0.9;
    window.speechSynthesis.speak(u);
  }

  const FILTERS = [
    { value: "all",      label: "All",       count: sessions.length },
    { value: "scenario", label: "Scenarios", count: sessions.filter((s) => s.mode === "scenario").length },
    { value: "topic",    label: "Topics",    count: sessions.filter((s) => s.mode === "topic").length },
    { value: "free",     label: "Free talk", count: sessions.filter((s) => s.mode === "free").length },
  ];

  return (
    <div className="page-enter" style={{ paddingBottom: 48 }}>
      <Chip tone="gold">History</Chip>
      <h1 className="font-display" style={{ fontSize: "clamp(40px, 6vw, 56px)", lineHeight: 1.05, fontWeight: 400, letterSpacing: "-0.025em", margin: "12px 0 8px" }}>
        Your past <em style={{ color: "var(--gold-500)" }}>sessions.</em>
      </h1>
      <p style={{ color: "var(--fg-2)", fontSize: 16, maxWidth: "60ch", margin: "0 0 32px", lineHeight: 1.6 }}>
        Replay AI lines, search the transcripts, see where you slipped.
      </p>

      {/* Stats */}
      <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 24 }}>
        <StatRow value={stats.sessionCount}     label="Sessions" />
        <StatRow value={stats.userMessages}     label="You spoke" />
        <StatRow value={stats.totalCorrections} label="Tips" />
        <StatRow value={stats.totalMessages}    label="Total messages" />
      </div>

      {/* Search + filters */}
      <div style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: 1, minWidth: 240 }}>
          <Search size={14} strokeWidth={1.5} color="var(--fg-3)" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search sessions and messages…"
            style={{ width: "100%", padding: "10px 12px 10px 34px", border: "1px solid var(--border-2)", borderRadius: "var(--radius-md)", background: "#fff", fontSize: 14, fontFamily: "var(--font-body)", outline: "none" }}
          />
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setFilter(f.value)}
              style={{
                padding: "8px 14px",
                background: filter === f.value ? "var(--ink-900)" : "#fff",
                color: filter === f.value ? "var(--paper)" : "var(--fg-2)",
                border: `1px solid ${filter === f.value ? "var(--ink-900)" : "var(--border-2)"}`,
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 150ms cubic-bezier(.2,.8,.2,1)",
              }}
            >
              {f.label} <span style={{ opacity: 0.65 }}>({f.count})</span>
            </button>
          ))}
        </div>
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <Card paperDeep style={{ textAlign: "center", padding: "48px 24px" }}>
          <Mic size={32} strokeWidth={1.5} color="var(--fg-3)" />
          <h3 className="font-display" style={{ fontSize: 26, fontWeight: 400, margin: "12px 0 6px" }}>No sessions yet.</h3>
          <p style={{ color: "var(--fg-3)", fontSize: 14, margin: "0 0 18px" }}>Start a conversation to see it here.</p>
          <Link to="/speak-practice" style={{ textDecoration: "none" }}>
            <Button variant="primary" icon={<Mic size={14} strokeWidth={1.8} />}>Start your first session</Button>
          </Link>
        </Card>
      )}

      {/* Sessions list — flat with hairlines */}
      <div>
        {filtered.map((s) => {
          const userMsgs = s.messages?.filter((m) => m.role === "user").length || 0;
          const lastMsg = s.messages?.[s.messages.length - 1]?.text || "";
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setOpenSession(s)}
              style={{
                display: "flex", gap: 16, alignItems: "flex-start",
                width: "100%", padding: "16px 0",
                borderBottom: "1px solid var(--border-1)",
                background: "transparent", border: 0, cursor: "pointer",
                textAlign: "left", fontFamily: "var(--font-body)",
                transition: "background 150ms cubic-bezier(.2,.8,.2,1)",
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "var(--paper-deep)"}
              onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
            >
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--fg-3)", flexShrink: 0, paddingTop: 2, minWidth: 70 }}>
                {formatTime(s.startedAt)}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                  <Chip tone={MODE_TONE[s.mode] || "neutral"}>{s.mode}</Chip>
                  <p style={{ fontWeight: 600, fontSize: 14, color: "var(--fg-1)", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {s.title}
                  </p>
                </div>
                <p style={{ color: "var(--fg-3)", fontSize: 13, margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {lastMsg.slice(0, 100)}{lastMsg.length > 100 ? "…" : ""}
                </p>
                <p style={{ fontSize: 11, color: "var(--fg-3)", margin: "6px 0 0", fontWeight: 500 }}>
                  {userMsgs} replies · {durationOf(s)}
                  {s.correctionCount > 0 && ` · ${s.correctionCount} tips`}
                </p>
              </div>
              <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                <button type="button" onClick={(e) => handleDelete(s.id, e)} aria-label="Delete"
                  style={{ background: "transparent", border: 0, color: "var(--fg-3)", cursor: "pointer", padding: 6, borderRadius: 999 }}>
                  <Trash2 size={14} strokeWidth={1.5} />
                </button>
                <ChevronRight size={16} strokeWidth={1.5} color="var(--fg-3)" />
              </span>
            </button>
          );
        })}
      </div>

      {sessions.length > 0 && (
        <div style={{ marginTop: 32, textAlign: "center" }}>
          <button type="button" onClick={handleClearAll}
            style={{ background: "transparent", border: 0, color: "var(--fg-3)", fontSize: 12, cursor: "pointer", textDecoration: "underline" }}>
            Clear all sessions
          </button>
        </div>
      )}

      {/* Session detail modal */}
      {openSession && (
        <div onClick={() => setOpenSession(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(11,17,29,0.5)", backdropFilter: "blur(6px) saturate(.7)", zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", border: "1px solid var(--border-2)", borderRadius: "var(--radius-xl)", boxShadow: "var(--shadow-modal)", maxWidth: 640, width: "100%", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
            <header style={{ padding: 20, borderBottom: "1px solid var(--border-1)", display: "flex", justifyContent: "space-between" }}>
              <div>
                <h2 className="font-display" style={{ fontSize: 26, fontWeight: 400, margin: "0 0 4px" }}>{openSession.title}</h2>
                <p style={{ fontSize: 12, color: "var(--fg-3)", margin: 0 }}>
                  {new Date(openSession.startedAt).toLocaleString()} · {durationOf(openSession)} · {openSession.correctionCount || 0} tips
                </p>
              </div>
              <button type="button" onClick={() => setOpenSession(null)} aria-label="Close"
                style={{ background: "transparent", border: 0, padding: 6, cursor: "pointer", color: "var(--fg-3)" }}>
                <X size={18} strokeWidth={1.6} />
              </button>
            </header>
            <main style={{ flex: 1, overflowY: "auto", padding: 20 }}>
              {openSession.messages?.map((m, i) => (
                <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", marginBottom: 12 }}>
                  <div style={{
                    maxWidth: "80%",
                    padding: "10px 14px",
                    borderRadius: "var(--radius-md)",
                    background: m.role === "user" ? "var(--ink-900)" : "var(--paper-deep)",
                    color: m.role === "user" ? "var(--paper)" : "var(--fg-1)",
                    borderTopRightRadius: m.role === "user" ? 4 : "var(--radius-md)",
                    borderTopLeftRadius: m.role !== "user" ? 4 : "var(--radius-md)",
                  }}>
                    <p style={{ fontSize: 14, lineHeight: 1.55, margin: 0, whiteSpace: "pre-wrap" }}>{m.text}</p>
                    {m.role === "ai" && (
                      <button type="button" onClick={() => speak(m.text)}
                        style={{ marginTop: 8, display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: "var(--gold-700)", background: "transparent", border: 0, cursor: "pointer", padding: 0 }}>
                        <Volume2 size={12} strokeWidth={1.5} /> Replay
                      </button>
                    )}
                    {m.correction && (
                      <div style={{ marginTop: 8, padding: "8px 12px", background: "rgba(200,154,44,0.12)", border: "1px solid rgba(200,154,44,0.30)", borderRadius: 6, fontSize: 12, color: "var(--gold-700)" }}>
                        <strong>Tip:</strong> {m.correction}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </main>
            <footer style={{ padding: 16, borderTop: "1px solid var(--border-1)" }}>
              <Link
                to={`/speak-practice${openSession.scenarioId ? `?scenario=${openSession.scenarioId}` : openSession.topicId ? `?topic=${openSession.topicId}` : ""}`}
                onClick={() => setOpenSession(null)}
                style={{ textDecoration: "none" }}
              >
                <Button variant="primary" style={{ width: "100%", justifyContent: "center" }}>
                  Continue practicing
                </Button>
              </Link>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

function StatRow({ value, label }) {
  return (
    <div style={{ background: "var(--paper-deep)", border: "1px solid var(--border-2)", borderRadius: "var(--radius-md)", padding: 14 }}>
      <p className="font-display" style={{ fontSize: 32, lineHeight: 1, margin: 0, color: "var(--ink-900)", fontWeight: 400 }}>{value}</p>
      <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.14em", color: "var(--fg-3)", margin: "6px 0 0" }}>{label}</p>
    </div>
  );
}
